/**
 * V189 → V82.7 Phase 3.2: 월별 휴가 사용량 조회 훅 (callable 전환)
 *
 * 정책:
 * - leaveTypeCode 의 used/cancelled/adjustment 이력 집계 (V82.5/V189/V311 부호정책)
 * - source='import' 는 importTotal 만 합산, 월별 미분배
 * - 부호 정책은 BE 단일 SSoT (`getMonthlyLeaveUsage`) 사용
 *
 * V82.7 Phase 3.2:
 *   - Firestore direct read 모두 제거
 *   - hook 반환 shape (MonthlyUsageResult / usageMap) 동일 유지 — UI 변경 0
 */

import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export interface MonthlyUsageResult {
  monthlyUsage: Record<number, number>;
  importTotal: number;
  normalTotal: number;
  grandTotal: number;
  isLoading: boolean;
  error: Error | null;
}

export interface PeriodInfo {
  validFrom?: string;
  validUntil?: string;
  periodStart?: string;
  periodEnd?: string;
}

interface BackendCore {
  monthlyUsage: Record<number, number>;
  importTotal: number;
  normalTotal: number;
  grandTotal: number;
}

interface SingleResp {
  success: boolean;
  result: BackendCore;
}
interface BatchResp {
  success: boolean;
  usageMap: Record<string, BackendCore>;
}

/**
 * V82.7: 단일 직원 월별 휴가 사용량 (callable 호출)
 */
export function useLeaveMonthlyUsage(
  employeeId: string | undefined,
  period: PeriodInfo | undefined,
  leaveTypeCode: string = 'annual'
): MonthlyUsageResult {
  const [data, setData] = useState<BackendCore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // BE 가 fallback period 처리하므로 FE 는 그대로 전달
  const validFrom = period?.validFrom;
  const validUntil = period?.validUntil;
  const periodStart = period?.periodStart;
  const periodEnd = period?.periodEnd;

  useEffect(() => {
    if (!employeeId) {
      setIsLoading(false);
      return;
    }
    let abort = false;
    const fetch = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<any, SingleResp>(getFunctions(), 'getMonthlyLeaveUsage');
        const r = await fn({
          mode: 'single',
          employeeId,
          leaveTypeCode,
          validFrom,
          validUntil,
          periodStart,
          periodEnd,
        });
        if (abort) return;
        setData(r.data.result);
      } catch (err) {
        if (!abort) {
          console.error('[V82.7] getMonthlyLeaveUsage(single) 실패:', err);
          setError(err as Error);
        }
      } finally {
        if (!abort) setIsLoading(false);
      }
    };
    fetch();
    return () => { abort = true; };
  }, [employeeId, leaveTypeCode, validFrom, validUntil, periodStart, periodEnd]);

  return useMemo<MonthlyUsageResult>(() => ({
    monthlyUsage: data?.monthlyUsage || {},
    importTotal: data?.importTotal || 0,
    normalTotal: data?.normalTotal || 0,
    grandTotal: data?.grandTotal || 0,
    isLoading,
    error,
  }), [data, isLoading, error]);
}

/**
 * V82.7: 다중 직원 월별 휴가 사용량 일괄 조회 (callable 호출)
 *  - HrLeaveOverviewPage 등 batch 사용처에서 호출
 *  - refreshKey 변경 시 강제 재조회
 */
export function useLeaveMonthlyUsageBatch(
  employeeIds: string[],
  periods: Record<string, PeriodInfo>,
  leaveTypeCode: string = 'annual',
  refreshKey: number = 0
): {
  usageMap: Record<string, MonthlyUsageResult>;
  isLoading: boolean;
  error: Error | null;
} {
  const [usageMap, setUsageMap] = useState<Record<string, MonthlyUsageResult>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 의존성 안정화
  const idsKey = employeeIds.join(',');
  const periodsKey = JSON.stringify(periods);

  useEffect(() => {
    if (employeeIds.length === 0) {
      setUsageMap({});
      setIsLoading(false);
      return;
    }
    let abort = false;
    const fetch = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const employees = employeeIds.map(eid => {
          const p = periods[eid] || {};
          return {
            employeeId: eid,
            validFrom: p.validFrom,
            validUntil: p.validUntil,
            periodStart: p.periodStart,
            periodEnd: p.periodEnd,
          };
        });

        const fn = httpsCallable<any, BatchResp>(getFunctions(), 'getMonthlyLeaveUsage');
        const r = await fn({
          mode: 'batch',
          employees,
          leaveTypeCode,
        });
        if (abort) return;
        const m = r.data.usageMap || {};
        const mapped: Record<string, MonthlyUsageResult> = {};
        for (const [k, v] of Object.entries(m)) {
          mapped[k] = {
            monthlyUsage: v.monthlyUsage || {},
            importTotal: v.importTotal || 0,
            normalTotal: v.normalTotal || 0,
            grandTotal: v.grandTotal || 0,
            isLoading: false,
            error: null,
          };
        }
        setUsageMap(mapped);
      } catch (err) {
        if (!abort) {
          console.error('[V82.7] getMonthlyLeaveUsage(batch) 실패:', err);
          setError(err as Error);
        }
      } finally {
        if (!abort) setIsLoading(false);
      }
    };
    fetch();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, leaveTypeCode, periodsKey, refreshKey]);

  return { usageMap, isLoading, error };
}

export default useLeaveMonthlyUsage;
